import type { Drone, Violation } from './types'

const DEFAULT_BASE_URL = 'http://localhost:8000'

// Runtime config (injected by docker-entrypoint.sh in production)
declare global {
  interface Window {
    APP_CONFIG?: {
      apiEndpoint?: string
      nfzSecretKey?: string
    }
  }
}

// Get API base URL from config or env or default
// In production with nginx reverse proxy, apiEndpoint should be empty (same origin)
// The nginx proxies /api/* to the backend
function getApiBaseUrl(): string {
  const configUrl = window.APP_CONFIG?.apiEndpoint?.trim()
  if (configUrl !== undefined) {
    // If explicitly set (even to empty string), use it
    return configUrl || ''
  }
  // Fallback to Vite env var or default
  return import.meta.env.VITE_API_BASE_URL?.trim() || DEFAULT_BASE_URL
}

function getNfzSecretKey(): string {
  return (
    window.APP_CONFIG?.nfzSecretKey?.trim() ||
    import.meta.env.VITE_NFZ_SECRET_KEY?.trim() ||
    ''
  )
}

export const API_BASE_URL = getApiBaseUrl()
export const NFZ_SECRET_KEY = getNfzSecretKey()

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }
  return response.json() as Promise<T>
}

export async function fetchDrones(signal?: AbortSignal): Promise<Drone[]> {
  // In production (nginx reverse proxy), API is at /api/
  // In development, API is at http://localhost:8000/
  const path = API_BASE_URL ? `${API_BASE_URL}/drones` : '/api/drones'
  const response = await fetch(path, { signal })
  return parseJson<Drone[]>(response)
}

export async function fetchViolations(signal?: AbortSignal): Promise<Violation[]> {
  const headers: Record<string, string> = {}

  if (NFZ_SECRET_KEY) {
    headers['X-Secret'] = NFZ_SECRET_KEY
  }

  const path = API_BASE_URL ? `${API_BASE_URL}/nfz` : '/api/nfz'
  const response = await fetch(path, { headers, signal })
  return parseJson<Violation[]>(response)
}

export async function checkHealth(signal?: AbortSignal): Promise<boolean> {
  const path = API_BASE_URL ? `${API_BASE_URL}/health` : '/api/health'
  const response = await fetch(path, { signal })
  return response.ok
}